import React, { useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';

import s from './index.module.css';

interface IDropdownProps {
  isExpanded: boolean;
  onRequireClose?: () => void;
  children: React.ReactNode;
  target: React.ReactElement;
  // this ref is not used here but can be used in
  // other dropdown implementations (ex. based on portal) to get position of target element
  // eslint-disable-next-line react/no-unused-prop-types
  targetRef: React.MutableRefObject<HTMLElement | null> | null;
  dataTestId?: string;
}

const SimpleDropdown = (props: IDropdownProps) => {
  const { target, children, onRequireClose, isExpanded, dataTestId = 'dropdown' } = props;

  const popupRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickBody = (event: MouseEvent) => {
      if (isExpanded && !rootRef.current?.contains(event.target as any)) {
        onRequireClose?.();
      }
    };

    window?.document.body.addEventListener('click', handleClickBody);

    return () => {
      window?.document.body.removeEventListener('click', handleClickBody);
    };
  }, [isExpanded, onRequireClose]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        onRequireClose?.();
      }
    },
    [onRequireClose],
  );

  return (
    <div
      className={s.simpleDropdown}
      ref={rootRef}
      onKeyDown={handleKeyDown}
      data-test-id={`${dataTestId}__wrapper`}
    >
      {target}
      <div
        data-test-id={`${dataTestId}__over`}
        className={cn(s.popup, { [s.isExpanded]: isExpanded })}
        ref={popupRef}
      >
        {children}
      </div>
    </div>
  );
};

export type { IDropdownProps };
export { SimpleDropdown };
